import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  CircleDollarSign,
  ClipboardList,
  ExternalLink,
  FileText,
  Link2,
  MessageSquareText,
  Paperclip,
} from "lucide-react";
import { PtsPurchaseApprovalService } from "@/services/APIs";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  PurchaseApprovalPurchaseStatusBadge,
  PurchaseApprovalStatusBadge,
} from "./StatusBadge";
import { formatMoney, resolveName } from "./utils";
import { formatDateDmy } from "../LeaveRequests/utils";

type PurchaseApprovalViewModalProps = {
  id: string | null;
  open: boolean;
  projectNameById: Map<string, string>;
  employeeNameById: Map<string, string>;
  onOpenChange: (open: boolean) => void;
};

function displayValue(value?: string | number | null): string {
  const text = String(value ?? "").trim();
  return text || "—";
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return formatDateDmy(value);
  }
  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 border-b border-gray-100 bg-slate-50 px-4 py-2.5">
        <span className="text-gray-500">{icon}</span>
        <h3 className="text-sm font-semibold text-[#111827]">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function DetailField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </p>
      <div className="mt-1.5 text-sm font-medium text-[#111827]">
        {children}
      </div>
    </div>
  );
}

const PurchaseApprovalViewModal = ({
  id,
  open,
  projectNameById,
  employeeNameById,
  onOpenChange,
}: PurchaseApprovalViewModalProps) => {
  const { data: item, isLoading } = useQuery({
    queryKey: ["purchase-approvals", "detail", id],
    queryFn: () => PtsPurchaseApprovalService.getPurchaseApprovalById(id!),
    enabled: open && Boolean(id),
  });

  const projectName =
    item?.project_name ?? resolveName(item?.project, projectNameById);
  const requestedByName =
    item?.requested_by_name ??
    resolveName(item?.requested_by, employeeNameById);
  const attachments = item?.attachments ?? [];
  const attachmentsLink = String(item?.attachments_link ?? "").trim();
  const managementReason = String(item?.management_reason ?? "").trim();
  const accountReason = String(item?.account_reason ?? "").trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[95vw] max-w-3xl flex-col overflow-hidden bg-[#f8fafc] p-0">
        <DialogHeader className="flex-shrink-0 border-b border-gray-200 bg-white px-6 pb-4 pt-6">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <DialogTitle className="text-lg font-semibold text-[#111827]">
                Purchase request details
              </DialogTitle>
              {item?.pr_number ? (
                <p className="mt-1 text-sm text-gray-500">{item.pr_number}</p>
              ) : null}
            </div>
            {/* {item ? (
              <div className="flex flex-wrap items-center gap-2">
                <PurchaseApprovalStatusBadge status={item.status} />
                <PurchaseApprovalPurchaseStatusBadge
                  purchaseStatus={item.purchase_status}
                />
              </div>
            ) : null} */}
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoading ? (
            <p className="py-10 text-center text-sm text-gray-500">
              Loading purchase request...
            </p>
          ) : !item ? (
            <p className="py-10 text-center text-sm text-gray-500">
              Purchase request details could not be loaded.
            </p>
          ) : (
            <div className="space-y-4">
              <SectionCard
                title="General information"
                icon={<ClipboardList className="h-4 w-4" />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailField label="PR Number">
                    {displayValue(item.pr_number)}
                  </DetailField>
                  <DetailField label="Title">
                    {displayValue(item.title)}
                  </DetailField>
                  <DetailField label="Project">{projectName}</DetailField>
                  <DetailField label="Priority">
                    {displayValue(item.priority)}
                  </DetailField>
                  <DetailField label="Requested By">
                    {requestedByName}
                  </DetailField>
                </div>
              </SectionCard>

              <SectionCard
                title="Dates"
                icon={<Calendar className="h-4 w-4" />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailField label="Requested Date">
                    {formatDateDmy(item.requested_date)}
                  </DetailField>
                  <DetailField label="Expected Date">
                    {formatDateDmy(item.expected_date)}
                  </DetailField>
                  <DetailField label="Created On">
                    {formatDateTime(item.created_on)}
                  </DetailField>
                  <DetailField label="Updated On">
                    {formatDateTime(item.updated_on)}
                  </DetailField>
                </div>
              </SectionCard>

              <SectionCard
                title="Cost & frequency"
                icon={<CircleDollarSign className="h-4 w-4" />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <DetailField label="Cost">
                    {formatMoney(item.cost, item.currency)}
                  </DetailField>
                  <DetailField label="Currency">
                    {displayValue(item.currency)}
                  </DetailField>
                  <DetailField label="Frequency">
                    {displayValue(item.frequency)}
                  </DetailField>
                </div>
              </SectionCard>

              <SectionCard
                title="Description"
                icon={<FileText className="h-4 w-4" />}
              >
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#374151]">
                  {displayValue(item.description)}
                </p>
              </SectionCard>

              <SectionCard
                title="Attachments"
                icon={<Paperclip className="h-4 w-4" />}
              >
                <div className="space-y-3">
                  <DetailField label="Attachments Link">
                    {attachmentsLink ? (
                      <a
                        href={attachmentsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-medium text-blue-600 hover:underline"
                      >
                        <Link2 className="h-3.5 w-3.5 shrink-0" />
                        <span className="max-w-[420px] truncate">
                          {attachmentsLink}
                        </span>
                        <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                      </a>
                    ) : (
                      "—"
                    )}
                  </DetailField>

                  <div>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                      Files
                    </p>
                    {attachments.length === 0 ? (
                      <p className="mt-1.5 text-sm text-[#111827]">—</p>
                    ) : (
                      <ul className="mt-2 space-y-2 pl-0">
                        {attachments.map((file, index) => {
                          const sizeLabel = formatFileSize(file.file_size);
                          return (
                            <li
                              key={file.document_id ?? `${file.name}-${index}`}
                              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-slate-50 px-3 py-2"
                            >
                              {file.file_type?.startsWith("image/") ? (
                                <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                              ) : (
                                <Paperclip className="h-4 w-4 shrink-0 text-gray-400" />
                              )}
                              {file.url ? (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="min-w-0 truncate text-sm font-medium text-blue-600 hover:underline"
                                >
                                  {file.name ?? "Attachment"}
                                </a>
                              ) : (
                                <span className="min-w-0 truncate text-sm text-[#111827]">
                                  {file.name ?? "Attachment"}
                                </span>
                              )}
                              {sizeLabel ? (
                                <span className="ml-auto shrink-0 text-xs text-gray-400">
                                  {sizeLabel}
                                </span>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              </SectionCard>

              <SectionCard
                title="Reasons"
                icon={<MessageSquareText className="h-4 w-4" />}
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-rose-100 bg-rose-50/60 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">
                        Management reason
                      </p>
                      <PurchaseApprovalStatusBadge status={item.status} />
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#374151]">
                      {displayValue(managementReason)}
                    </p>
                  </div>
                  <div className="rounded-lg border border-sky-100 bg-sky-50/60 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-sky-600">
                        Account reason
                      </p>
                      <PurchaseApprovalPurchaseStatusBadge
                        purchaseStatus={item.purchase_status}
                      />
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#374151]">
                      {displayValue(accountReason)}
                    </p>
                  </div>
                </div>
              </SectionCard>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t border-gray-200 bg-white px-6 py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseApprovalViewModal;
